import { Box } from '@mui/material';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import DisabledByDefaultOutlinedIcon from '@mui/icons-material/DisabledByDefaultOutlined';
import { SvgIconComponent } from '@mui/icons-material';
import colors from '../../../../assets/theme/base/colors';
import { DeviationsDto } from '../../../../Component/Model/Dto/EmploymentEquity/Deviations';

interface CardStyles {
    bg: string;
    iconBg: string;
    text: string;
    Icon: SvgIconComponent;
}

function getCardStyles(isPositive: boolean): CardStyles {
    if (isPositive) {
        return {
            bg: colors.deviationSeverity.onTrack.bg,
            iconBg: colors.deviationSeverity.onTrack.iconBg,
            text: colors.deviationSeverity.onTrack.icon,
            Icon: CheckCircleOutlinedIcon,
        };
    }
    return {
        bg: colors.deviationSeverity.critical.bg,
        iconBg: colors.deviationSeverity.critical.iconBg,
        text: colors.deviationSeverity.critical.icon,
        Icon: DisabledByDefaultOutlinedIcon,
    };
}

interface StatCardShellProps {
    styles: CardStyles;
    children: React.ReactNode;
}

function StatCardShell({ styles, children }: StatCardShellProps) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'stretch', backgroundColor: styles.bg, borderRadius: '8px', overflow: 'hidden', minHeight: 56 }}>
            <Box sx={{ width: 36, flexShrink: 0, backgroundColor: styles.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <styles.Icon sx={{ width: 20, height: 20, color: colors.white.main }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, flex: 1 }}>
                {children}
            </Box>
        </Box>
    );
}

interface JustificationSummaryStatCardsProps {
    summary: DeviationsDto.JustificationSummaryItemDto;
}

export function JustificationSummaryStatCards({ summary }: JustificationSummaryStatCardsProps) {
    const keyReasonStyles: CardStyles = getCardStyles(summary.eea2KeyJustifiableReasonCaptured);
    const keyReasonLabel: string = summary.eea2KeyJustifiableReasonCaptured
        ? 'EEA2 key justifiable reason captured'
        : 'EEA2 key justifiable reason not captured';

    const documentsStyles: CardStyles = getCardStyles(summary.withoutDocumentsCount === 0);
    const documentsDescription: string = summary.withoutDocumentsCount > 0
        ? 'justifications missing attachments'
        : 'all justifications have documents';

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, my: 3 }}>
            <StatCardShell styles={keyReasonStyles}>
                <Box sx={{ fontSize: '0.875rem', fontWeight: 600, color: keyReasonStyles.text, lineHeight: 1.3 }}>
                    {keyReasonLabel}
                </Box>
            </StatCardShell>

            <StatCardShell styles={documentsStyles}>
                <Box sx={{ fontSize: '1.5rem', fontWeight: 700, lineHeight: 1, color: documentsStyles.text, flexShrink: 0 }}>
                    {summary.withoutDocumentsCount}
                </Box>
                <Box>
                    <Box sx={{ fontSize: '0.875rem', fontWeight: 600, color: documentsStyles.text, lineHeight: 1.3 }}>
                        Without Documents
                    </Box>
                    <Box sx={{ fontSize: '0.75rem', color: documentsStyles.text, opacity: 0.75, lineHeight: 1.3 }}>
                        {documentsDescription}
                    </Box>
                </Box>
            </StatCardShell>
        </Box>
    );
}
